import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { STORIES, FAQS } from "@/data/content";
import { SectionHead } from "@/components/Blocks";
import { Reveal } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const StoryCard = ({ s, i, big }) => (
  <Link
    to={`/client-stories#${s.id}`}
    data-testid={`story-card-${i + 1}`}
    className={`group relative flex flex-col justify-between h-full p-8 sm:p-10 bg-[#131B2E] border border-[#C5A059]/20 hover:border-[#C5A059]/50 hover:bg-[#1A253E] transition-[background-color,border-color] duration-500 overflow-hidden ${big ? "min-h-[520px]" : "min-h-[250px]"}`}
  >
    <span className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#6B2D8F]/0 group-hover:bg-[#6B2D8F]/25 blur-[80px] transition-colors duration-700" />
    <div className="relative flex items-center justify-between">
      <span className="font-data text-[11px] tracking-[0.25em] uppercase text-[#C5A059]">{s.code} · {s.profile}</span>
      <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-[#E2C68C] group-hover:rotate-45 transition-[color,transform] duration-500" />
    </div>
    <div className="relative mt-10">
      {big && <Quote className="w-10 h-10 text-[#C5A059]/60 mb-6" />}
      <h3 className={`font-display text-slate-50 leading-[1.1] ${big ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{big ? s.quote : s.title}</h3>
      {big && <p className="mt-6 text-slate-300 text-sm sm:text-base leading-relaxed max-w-lg">{s.excerpt}</p>}
      <p className="mt-6 font-data text-[11px] tracking-[0.2em] uppercase text-slate-400">{s.setting}</p>
    </div>
  </Link>
);

export const StoriesPreview = () => (
  <section data-testid="client-stories-section" className="bg-[#0B1120] py-24 sm:py-36">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <SectionHead eyebrow="Anonymised client stories" lines={["Three people.", "Three new beginnings."]} />
        <Reveal>
          <Link to="/client-stories" data-testid="stories-view-all-link" className="text-sm text-[#E2C68C] link-underline pb-1 inline-flex items-center gap-2">
            Read all stories <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-16 grid lg:grid-cols-2 gap-4">
        <Reveal className="h-full"><StoryCard s={STORIES[0]} i={0} big /></Reveal>
        <div className="grid gap-4">
          <Reveal delay={0.1} className="h-full"><StoryCard s={STORIES[1]} i={1} /></Reveal>
          <Reveal delay={0.2} className="h-full"><StoryCard s={STORIES[2]} i={2} /></Reveal>
        </div>
      </div>
      <p className="mt-6 text-xs text-slate-400">All client stories are anonymised to protect confidentiality.</p>
    </div>
  </section>
);

export const Faq = () => (
  <section data-testid="faq-section" className="bg-[#070A12] py-24 sm:py-36">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="Questions, answered" lines={["What clients", "ask us first."]} />
      </div>
      <Reveal className="lg:col-span-7">
        <Accordion type="single" collapsible defaultValue="item-0" className="border-t border-[#C5A059]/20">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-white/10">
              <AccordionTrigger data-testid={`faq-item-${i + 1}`} className="py-7 text-left font-display text-xl sm:text-2xl text-slate-100 hover:no-underline hover:text-[#E2C68C] [&>svg]:text-[#C5A059]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
